<script>
import { BLOCO_PROXIMOS_PASSOS } from '@modules/aawz-intelligence-v2/constants/TextChat'

// Aceita os DOIS formatos de `parecer.proximos_passos`: `list[str]` (o que o
// backend devolve hoje) e `{title, description, badge}` (contrato do gap G4) —
// assim o dia da virada não exige deploy de frontend.
//
// Morava em `utils/painel-eventos.js`, que saiu no merge com o
// `desenvolvimento` (o redutor de painel que ficou foi o do #1961). Como este é
// o único consumidor e a função não tem estado, veio para cá em vez de virar um
// util de uma linha só.
function normalizarProximosPassos(lista) {
  const itens = Array.isArray(lista) ? lista : []
  return itens.map(function (item) {
    if (typeof item === 'string') {
      return { title: item, description: '', badge: '' }
    }
    return {
      title: (item && item.title) || '',
      description: (item && item.description) || '',
      badge: (item && item.badge) || '',
    }
  })
}

// A etiqueta vem do backend como texto livre ("Opcional Neste Ciclo",
// "Obrigatório", ...), entao a cor sai de palavra-chave, nao de enum. Sem
// correspondencia, neutro — que e o certo para uma etiqueta desconhecida.
const RADICAL_OBRIGATORIO = 'obrigat'
const RADICAL_RECOMENDADO = 'recomend'

// Função de módulo, e não método: o resultado é pré-computado no `itens()` e o
// template só lê `item.classeBadge`. Como método chamado no `v-for`, rodava a
// cada tick do streaming, sem cache (regra "Sem Chamada de Função no Template").
function classeBadge(badge) {
  const texto = String(badge || '').toLowerCase()
  if (texto.includes(RADICAL_OBRIGATORIO)) {
    return 'next-steps__badge--obrigatorio'
  }
  if (texto.includes(RADICAL_RECOMENDADO)) {
    return 'next-steps__badge--recomendado'
  }
  return ''
}

export default {
  name: 'IntelligenceV2NextSteps',
  props: {
    // `parecer.proximos_passos`. Aceita `list[str]` (o que o backend devolve
    // hoje) e `{title, description, badge}` (contrato do gap G4) — o
    // normalizador é o mesmo do redutor, então a virada do G4 não pede deploy
    // de frontend. Badge NÃO é renderizada enquanto o gap não fechar.
    passos: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  // [11/09/2026] O estado "carregando" ("Estruturando próximos passos...")
  // SAIU: era um segundo indicador de trabalho no meio da resposta, ao lado
  // da linha única do raciocínio. O bloco só existe com passos.
  computed: {
    titulo() {
      return BLOCO_PROXIMOS_PASSOS.titulo
    },
    itens() {
      return normalizarProximosPassos(this.passos).map(function (
        passo,
        indice
      ) {
        // Índice na chave: dois passos podem ter o mesmo título e `:key`
        // duplicado gera warning do Vue. A lista é curta e não reordena.
        return {
          chave: passo.title + '-' + indice,
          ordem: indice + 1,
          passo,
          // Pré-computado aqui, e não chamado no template: o `v-for` roda a
          // cada tick de streaming, e método no template não tem cache.
          classeBadge: classeBadge(passo.badge),
        }
      })
    },
    // Lista vazia: o bloco não existe (nada de card vazio).
    mostraBloco() {
      return this.itens.length > 0
    },
  },
}
</script>

<template>
  <div v-if="mostraBloco" class="next-steps ia-block-card">
    <div class="ia-block-card__cabecalho ia-block-card__cabecalho--fita">
      <!-- `task-square` = o ListChecks do protótipo. -->
      <icon-svg
        name="task-square"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">
        {{ titulo }}
      </span>
    </div>

    <ol class="next-steps__lista">
      <li v-for="item in itens" :key="item.chave" class="next-steps__item">
        <span class="next-steps__ordem ia-texto-selo">{{ item.ordem }}</span>
        <span class="next-steps__corpo">
          <span class="next-steps__topo">
            <span class="next-steps__titulo ia-texto-auxiliar">{{ item.passo.title }}</span>
            <span
              v-if="item.passo.badge"
              class="next-steps__badge ia-texto-micro"
              :class="item.classeBadge"
              >{{ item.passo.badge }}</span
            >
          </span>
          <span v-if="item.passo.description" class="next-steps__descricao ia-texto-auxiliar">{{
            item.passo.description
          }}</span>
        </span>
      </li>
    </ol>
  </div>
</template>

<style lang="sass" scoped>
// Card, faixa e título vêm de `.ia-block-card*` (partial de módulo). Aqui fica
// só a lista numerada.

// `display: block` reafirmado: a regra global `.content-nav ul` (e a lista
// aninhada em `.content-nav`) impoe `display: flex`, e o chat vive dentro dela
// — sem isto os passos viram colunas lado a lado. Mesmo motivo do
// `list-style`, que o reset global `* { list-style: none }` ja zerou.
.next-steps__lista
  display: block
  margin: 0
  padding: 0
  list-style: none

// Separador entre passos vai ate a borda do card, como no prototipo — por isso
// a lista nao usa `.ia-block-card__corpo` e cada item paga o proprio padding.
.next-steps__item + .next-steps__item
  border-top: 1px solid var(--border-hairline)

.next-steps__item
  display: flex
  align-items: flex-start
  gap: 10px
  padding: 10px 14px

// Numero em disco, nao "1." solto: e o que separa passo de item de lista.
.next-steps__ordem
  color: var(--neutral-700)
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 20px
  height: 20px
  margin-top: 1px
  border-radius: 50%
  background: var(--neutral-100)

.next-steps__corpo
  display: flex
  flex-direction: column
  gap: 3px
  flex: 1 1 auto
  min-width: 0

.next-steps__topo
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 8px

// Pilula propria e nao BaseStatus: a variante `soft` do DS rebaixa o conteudo
// para minusculas e a etiqueta aqui e micro-caps. Mesmo motivo do badge de
// CONDUZ/APOIA no painel de deliberação.
.next-steps__badge
  color: var(--neutral-700)
  flex-shrink: 0
  border-radius: 999px
  padding: 1px 7px
  letter-spacing: 0.06em
  text-transform: uppercase
  background: var(--neutral-100)

.next-steps__badge--obrigatorio
  background: var(--warning-bg-soft)
  color: var(--warning-text-strong)

.next-steps__badge--recomendado
  background: var(--success-bg-soft)
  color: var(--success-text)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.next-steps__titulo
  color: var(--neutral-900)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.next-steps__descricao
  color: var(--neutral-700)

</style>
